import React from 'react';
import { Platform } from 'react-native';

import { Box, Text, Divider as UCLDivider } from '@fhs-legacy/universal-components';

import { ContentContainer } from './styled';
import theme from './theme';
import { IStoreDiagnosticData } from './types';

const CodeWrapper = Text.withConfig({
  bg: Styles.color.codeBlockBackground,
  color: theme.codeColor,
  justifyContent: 'center',
  paddingY: '1',
  paddingX: {
    base: '2',
    desktop: '4',
  },
  fontSize: 'xl',
  variant: 'mono',
  textAlign: 'center',
  fontWeight: 'light',
});

const Divider = UCLDivider.withConfig({
  marginY: '6',
});

const DiagnosticsMainTitle = Text.withConfig({
  textAlign: 'center',
  fontFamily: 'heading',
  fontWeight: 'normal',
  fontSize: '4xl',
  color: 'black',
  letterSpacing: '-0.42px',
  maxWidth: { desktop: '500px' },
  alignSelf: 'center',
  marginBottom: '6',
});

const DiagnosticsStoreTitle = Text.withConfig({
  textAlign: 'center',
  fontWeight: 'normal',
  marginBottom: '6',
  fontSize: 'xl',
});

const BoldMonospace = Text.withConfig({
  fontWeight: 'bold',
  fontSize: 'xl',
});

const Paragraph = Text.withConfig({
  textAlign: 'center',
});

const webFocusableSectionProps =
  Platform.OS === 'web'
    ? ({
        // Keyboard users can Tab through major diagnostic blocks inside the modal focus trap.
        _web: { tabIndex: 0 as 0 },
      } as const)
    : {};

export const determineStoreInfoSubheader = (
  storeName: string | undefined,
  storeAddress: string | undefined
) => {
  if (!storeName) {
    return `(${storeAddress})`;
  }

  if (!storeAddress || storeName.toLowerCase() === storeAddress.toLowerCase()) {
    return `(${storeName})`;
  }

  return `(${storeName} - ${storeAddress})`;
};

const DiagnosticModalContents: React.FC<
  React.PropsWithChildren<{
    storeDiagnosticData: IStoreDiagnosticData | undefined;
  }>
> = ({ storeDiagnosticData }) => {
  if (!storeDiagnosticData) {
    return (
      <ContentContainer>
        <DiagnosticsMainTitle
          accessibilityRole="header"
          accessibilityLevel={1}
          nativeID="diagnostics-empty-title"
        >
          No Diagnostic Data Available
        </DiagnosticsMainTitle>
        <p>Sorry, diagnostic data is not available at this time!</p>
      </ContentContainer>
    );
  }

  const storeSubheader = determineStoreInfoSubheader(
    storeDiagnosticData.restaurantName,
    storeDiagnosticData.restaurantStreetAddress
  );

  return (
    <ContentContainer>
      <Box
        marginBottom="$1"
        nativeID="diagnostics-screen-title-region"
        {...(Platform.OS === 'web'
          ? {
              accessibilityLabel: 'Store Diagnostics',
              _web: {
                tabIndex: 0 as 0,
                role: 'region' as const,
              },
            }
          : {})}
      >
        <DiagnosticsMainTitle
          accessible={Platform.OS === 'web' ? false : undefined}
          nativeID="diagnostics-screen-title"
          accessibilityRole="header"
          accessibilityLevel={1}
        >
          Store Diagnostics
        </DiagnosticsMainTitle>
      </Box>
      <Box
        marginBottom="$2"
        nativeID="diagnostics-store-subtitle-region"
        {...(Platform.OS === 'web'
          ? {
              accessibilityLabel: storeSubheader,
              _web: {
                tabIndex: 0 as 0,
                role: 'region' as const,
              },
            }
          : {})}
      >
        <DiagnosticsStoreTitle
          accessible={Platform.OS === 'web' ? false : undefined}
          nativeID="diagnostics-store-subtitle"
          accessibilityRole="header"
          accessibilityLevel={2}
        >
          {storeSubheader}
        </DiagnosticsStoreTitle>
      </Box>
      <Box
        accessibilityLabel="Online ordering flags. isRestaurantOpen, isMobileOrderingAvailable, and isRestaurantPosOnline must be true."
        {...webFocusableSectionProps}
      >
        <Paragraph>
          This store's online ordering should be enabled if all three of these properties are true
          (these are the current values for this store):
        </Paragraph>

        <CodeWrapper>{`isRestaurantOpen: ${storeDiagnosticData.isRestaurantOpen}`}</CodeWrapper>
        <CodeWrapper>{`isMobileOrderingAvailable: ${storeDiagnosticData.isMobileOrderingAvailable}`}</CodeWrapper>
        <CodeWrapper>{`isRestaurantPosOnline: ${storeDiagnosticData.isRestaurantPosOnline}`}</CodeWrapper>
      </Box>

      <Box
        accessibilityLabel="Country in physical address for Tim Hortons online ordering."
        {...webFocusableSectionProps}
      >
        <Paragraph>
          For Tim Hortons stores, the store must also have a country in its physical address to enable
          online ordering (here's the current value for this store):
        </Paragraph>

        <CodeWrapper>{`country: '${storeDiagnosticData.restaurantCountry}'`}</CodeWrapper>
      </Box>

      <Divider />
      <Box
        accessibilityLabel="isRestaurantOpen: driveThruOpen and diningRoomOpen values."
        {...webFocusableSectionProps}
      >
        <Paragraph>
          <BoldMonospace>isRestaurantOpen </BoldMonospace>
          is true if either of these store properties is true (here are the current values for this
          store):
        </Paragraph>
        <CodeWrapper>{`driveThruOpen: ${storeDiagnosticData.driveThruOpen}`}</CodeWrapper>
        <CodeWrapper>{`diningRoomOpen: ${storeDiagnosticData.diningRoomOpen}`}</CodeWrapper>
      </Box>

      <Divider />
      <Box
        accessibilityLabel="isMobileOrderingAvailable and hasMobileOrdering."
        {...webFocusableSectionProps}
      >
        <Paragraph>
          <BoldMonospace>isMobileOrderingAvailable </BoldMonospace>
          is set to the value of the store's <BoldMonospace>hasMobileOrdering </BoldMonospace>
          property (here's the current value for this store):
        </Paragraph>

        <CodeWrapper>{`hasMobileOrdering: ${storeDiagnosticData.restaurantHasMobileOrdering}`}</CodeWrapper>
      </Box>

      <Divider />
      <Box accessibilityLabel="isRestaurantPosOnline heartbeat check." {...webFocusableSectionProps}>
        <Paragraph>
          <BoldMonospace>isRestaurantPosOnline </BoldMonospace>is determined by a store's heartbeat
          check.
        </Paragraph>
      </Box>
    </ContentContainer>
  );
};

export default DiagnosticModalContents;
